import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null); // null loading, false invalid, obj valid
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then((r) => setInfo(r.data)).catch(() => setInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const r = await api.post(`/auth/invite/${token}/accept`, { password, name });
      setUser(r.data);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-sm bg-white flex items-center justify-center overflow-hidden">
            <img src="/synesis-icon.jpg" alt="Logo" className="h-full w-full object-contain" />
          </div>
          <span className="font-display font-bold text-lg">Synesis AI</span>
        </div>

        {info === null && <Loader2 className="h-6 w-6 animate-spin text-blue-700" />}

        {info === false && (
          <div className="text-center">
            <XCircle className="h-10 w-10 text-red-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-semibold">Invito non valido</h2>
            <p className="text-sm text-slate-500 mt-2">Il link è scaduto o non è più valido.</p>
            <Button variant="outline" className="mt-6" onClick={() => navigate("/login")}>Vai al login</Button>
          </div>
        )}

        {info && info.valid && (
          <>
            <span className="label-caps">Attivazione accesso</span>
            <h2 className="font-display text-2xl font-semibold tracking-tight mt-1 mb-1">Imposta la tua password</h2>
            <p className="text-sm text-slate-500 mb-6">
              Invito per <strong>{info.email}</strong>
              {info.org_nome ? <> · {info.org_nome}</> : null}
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome e cognome</Label>
                <Input id="name" value={name} data-testid="invite-name"
                  onChange={(e) => setName(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pwd">Password (min. 8 caratteri)</Label>
                <Input id="pwd" type="password" value={password} data-testid="invite-password"
                  onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="invite-error">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="invite-submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Attiva accesso
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
